<template>
    <div class="profile-container" v-if="user"
        :class="{ 'modal-mode': modalMode }"
    >
        <div class="profile-header">
            <h2 v-if="!modalMode">Profile Settings</h2>
            <button 
                @click="saveProfile" 
                class="save-btn top-save-btn"
                :disabled="isSaving"
            >
                {{ isSaving ? 'Saving...' : 'Save Changes' }}
            </button>
        </div>
        <ProfileCompleteness v-if="!modalMode" :profile="profile" />
        <PhotoUpload 
            :initial-photo-url="profile.photo_url || user.photoURL || DEFAULT_AVATAR"
            :user-id="photoUploadUserId"
            :is-admin="modalMode"
            @update:photo="updatePhoto"
            @error="handleImageError"
        />
        <div class="profile-form">
            <!-- Personal Information Section -->
            <div class="form-section">
                <h3>Personal Information</h3>
                <div class="form-grid">
                    <div class="form-group">
                        <label>First Name</label>
                        <input v-model="profile.firstName" type="text" class="form-input" required>
                    </div>
                    <div class="form-group">
                        <label>Last Name</label>
                        <input v-model="profile.lastName" type="text" class="form-input" required>
                    </div>
                </div>
                <div class="form-grid">
                    <div class="form-group">
                        <label>Preferred Name</label>
                        <input v-model="profile.displayName" type="text" class="form-input">
                    </div>
                    <div class="form-group">
                        <label>Birthday (Day/Month)</label>
                        <div class="birthday-input">
                            <input 
                                v-model="profile.birthDay"
                                type="number" 
                                min="1"
                                max="31"
                                placeholder="Day"
                                class="form-input day-input"
                            >
                            <select 
                                v-model="profile.birthMonth"
                                class="form-input month-input"
                            >
                                <option value="">Month</option>
                                <option value="1">January</option>
                                <option value="2">February</option>
                                <option value="3">March</option>
                                <option value="4">April</option>
                                <option value="5">May</option>
                                <option value="6">June</option>
                                <option value="7">July</option>
                                <option value="8">August</option>
                                <option value="9">September</option>
                                <option value="10">October</option>
                                <option value="11">November</option>
                                <option value="12">December</option>
                            </select>
                        </div>
                    </div>
                </div>
                <div class="form-group">
                    <label>Partner's Name</label>
                    <input v-model="profile.partner_name" type="text" class="form-input">
                </div>
            </div>

            <!-- Company Information Section -->
            <div class="form-section">
                <h3>Company Information</h3>
                <div class="form-grid">
                    <div class="form-group">
                        <label>Company Name</label>
                        <input v-model="profile.companyName" type="text" class="form-input">
                    </div>

                    <div class="form-group">
                        <label>Title/Role</label>
                        <input v-model="profile.title" type="text" class="form-input">
                    </div>
                </div>

                <div class="form-group">
                    <label>Company Logo</label>
                    <CompanyLogoUpload
                        :initial-logo-url="profile.companyLogoUrl"
                        :signed-in-user-id="props.currentUserId"
                        :target-user-id="props.targetUserId || props.currentUserId || user?.id"
                        :is-admin="!!props.targetUserId"
                        @update:logo="updateCompanyLogo"
                        @error="handleLogoError"
                    />
                </div>

                <div class="form-group">
                    <label>Company Website</label>
                    <input 
                        v-model="profile.companyWebsite" 
                        type="url" 
                        class="form-input"
                        placeholder="https://"
                    >
                </div>

                <div class="form-group">
                    <label>LinkedIn Profile</label>
                    <input 
                        v-model="profile.linkedinUrl" 
                        type="url" 
                        class="form-input"
                        placeholder="https://linkedin.com/in/..."
                    >
                </div>

                <div class="form-group">
                    <label>Area of Business</label>
                    <input v-model="profile.businessArea" type="text" class="form-input">
                </div>

                <div class="form-group">
                    <label>Company Bio</label>
                    <textarea 
                        v-model="profile.companyBio" 
                        class="form-input" 
                        rows="4"
                        placeholder="Tell us about your company..."
                    ></textarea>
                </div>
            </div>

            <!-- Referral Information Section -->
            <div class="form-section">
                <h3>Referral Information</h3>
                <div class="form-group">
                    <label>Ideal Customer</label>
                    <textarea 
                        v-model="profile.idealCustomer" 
                        class="form-input" 
                        rows="4"
                        placeholder="Describe your ideal customer or referral..."
                    ></textarea>
                </div>
                <div class="form-group">
                    <label>Avoid Referring</label>
                    <textarea 
                        v-model="profile.avoidReferring" 
                        class="form-input" 
                        rows="4"
                        placeholder="Describe the types of referrals you want to avoid..."
                    ></textarea>
                </div>
            </div>

            <!-- Contact Information Section -->
            <div class="form-section">
                <h3>Contact Information</h3>
                <div class="form-group">
                    <label>Phone Number</label>
                    <div class="phone-input">
                        <select v-model="profile.phone_country" class="country-select">
                            <option value="+61">🇦🇺 +61</option>
                            <option value="+64">🇳🇿 +64</option>
                        </select>
                        <input 
                            v-model="profile.phone_number" 
                            type="tel" 
                            class="form-input phone"
                            placeholder="412 345 678"
                            @input="formatPhoneNumber"
                        >
                    </div>
                </div>
            </div>

            <!-- About Section -->
            <div class="form-section">
                <h3>About</h3>
                <div class="form-group">
                    <label>Bio</label>
                    <textarea v-model="profile.bio" class="form-input" rows="4"></textarea>
                </div>
            </div>

            <!-- Catering Information Section -->
            <div class="form-section">
                <h3>Catering Information</h3>
                <div class="form-grid">
                    <div class="form-group">
                        <label>First Coffee Order</label>
                        <input 
                            v-model="profile.coffee_order_1" 
                            type="text" 
                            class="form-input"
                            placeholder="e.g., Large Flat White"
                        >
                    </div>
                    <div class="form-group">
                        <label>Second Coffee Order</label>
                        <input 
                            v-model="profile.coffee_order_2" 
                            type="text" 
                            class="form-input"
                            placeholder="e.g., Small Cappuccino"
                        >
                    </div>
                </div>
                <div class="form-group">
                    <label>Dietary Requirements</label>
                    <textarea 
                        v-model="profile.dietary_requirements" 
                        class="form-input"
                        placeholder="Please list any dietary requirements or restrictions"
                    ></textarea>
                </div>
            </div>

            <!-- Administrator Access Section -->
            <div class="form-section" v-if="profile.role === 'admin'">
                <h3>Access Level</h3>
                <div class="admin-toggle">
                    <label class="switch">
                        <input 
                            type="checkbox" 
                            v-model="isAdmin" 
                            @change="updateUserRole"
                        >
                        <span class="slider round"></span>
                    </label>
                    <span class="role-label">Administrator Access</span>
                </div>
            </div>

            <div class="form-actions">
                <button 
                    @click="saveProfile" 
                    class="save-btn"
                    :disabled="isSaving"
                >
                    {{ isSaving ? 'Saving...' : 'Save Changes' }}
                </button>
                <p v-if="error" class="error-message">{{ error }}</p>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
// Removed Firebase dependency
import { userService } from '../services/api';
import ProfileCompleteness from './ProfileCompleteness.vue';
import PhotoUpload from './PhotoUpload.vue';
import CompanyLogoUpload from './CompanyLogoUpload.vue';

export default {
    name: 'UserProfile',
    components: {
        ProfileCompleteness,
        PhotoUpload,
        CompanyLogoUpload
    },
    props: {
        currentUserId: {
            type: String,
            required: true
        },
        targetUserId: {
            type: String,
            required: false,
            default: null
        },
        targetDbId: {
            type: Number,
            required: false,
            default: null
        },
        userData: {
            type: Object,
            default: null
        },
        modalMode: {
            type: Boolean,
            default: false
        }
    },
    setup(props, { emit }) {
        const user = ref(null);
        const profile = ref({
            displayName: '',
            firstName: '',
            lastName: '',
            title: '',
            bio: '',
            companyName: '',
            companyWebsite: '',
            companyLogoUrl: '',
            companyBio: '',
            idealCustomer: '',
            avoidReferring: '',
            linkedinUrl: '',
            phone_number: '',
            phone_country: '+61',
            businessArea: '',
            photo_url: '',
            role: 'user',
            partner_name: '',
            birthDay: null,
            birthMonth: null,
            coffee_order_1: '',
            coffee_order_2: '',
            dietary_requirements: ''
        });
        const error = ref('');
        const isSaving = ref(false);
        const isAdmin = ref(false);

        const DEFAULT_AVATAR = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CiAgPGNpcmNsZSBjeD0iNTAiIGN5PSI1MCIgcj0iNTAiIGZpbGw9IiNFMEUwRTAiLz4KICA8Y2lyY2xlIGN4PSI1MCIgY3k9IjQwIiByPSIyMCIgZmlsbD0iI0JEQkRCRCIvPgogIDxwYXRoIGQ9Ik0yMCA4MEMyMCA2MCAzNSA2NSA1MCA2NUM2NSA2NSA4MCA2MCA4MCA4MEM4MCAxMDAgNjUgOTUgNTAgOTVDMzUgOTUgMjAgMTAwIDIwIDgwWiIgZmlsbD0iI0JEQkRCRCIvPgo8L3N2Zz4=';

        const loadUserData = async () => {
            try {
                let userData = props.userData;
                
                if (!userData) {
                    userData = props.modalMode ? 
                        await userService.getUser(props.userId) : 
                        await userService.getProfile();
                }
                
                if (userData) {
                    profile.value = {
                        displayName: userData.display_name || '',
                        firstName: userData.first_name || '',
                        lastName: userData.last_name || '',
                        email: userData.email,
                        title: userData.title || '',
                        bio: userData.bio || '',
                        companyName: userData.company_name || '',
                        companyWebsite: userData.company_website || '',
                        companyLogoUrl: userData.company_logo_url || '',
                        companyBio: userData.company_bio || '',
                        idealCustomer: userData.ideal_customer || '',
                        avoidReferring: userData.avoid_referring || '',
                        linkedinUrl: userData.linkedin_url || '',
                        businessArea: userData.business_area || '',
                        phone_number: userData.phone ? userData.phone.slice(3).replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3') : '',
                        phone_country: userData.phone ? userData.phone.slice(0, 3) : '+61',
                        photo_url: userData.photo_url || '',
                        role: userData.role || 'user',
                        birthDay: userData.birth_day,
                        birthMonth: userData.birth_month,
                        coffee_order_1: userData.coffee_order_1 || '',
                        coffee_order_2: userData.coffee_order_2 || '',
                        dietary_requirements: userData.dietary_requirements || '',
                        partner_name: userData.partner_name || ''
                    };
                }
                isAdmin.value = profile.value.role === 'admin';
                user.value = userData;
            } catch (error) {
                console.error('[UserProfile] Error loading profile:', error);
                error.value = 'Failed to load profile: ' + error.message;
            }
        };

        onMounted(async () => {
            await loadUserData();
        });

        const formatPhoneNumber = () => {
            if (!profile.value.phone_number) return;
            let number = profile.value.phone_number.replace(/\D/g, '');
            if (number.length > 9) number = number.substr(0, 9);
            
            // Format as XXX XXX XXX
            if (number.length > 6) {
                number = number.replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3');
            } else if (number.length > 3) {
                number = number.replace(/(\d{3})(\d{3})/, '$1 $2');
            }
            
            profile.value.phone_number = number;
        };

        const saveProfile = async () => {
            try {
                const userData = {
                    display_name: profile.value.displayName,
                    first_name: profile.value.firstName,
                    last_name: profile.value.lastName,
                    title: profile.value.title,
                    company_name: profile.value.companyName,
                    company_website: profile.value.companyWebsite || null,
                    business_area: profile.value.businessArea,
                    phone: profile.value.phone_number ? 
                        profile.value.phone_country + profile.value.phone_number.replace(/\D/g, '') :
                        null,
                    bio: profile.value.bio,
                    photo_url: profile.value.photo_url,
                    partner_name: profile.value.partner_name,
                    birth_day: profile.value.birthDay ? parseInt(profile.value.birthDay) : null,
                    birth_month: profile.value.birthMonth ? parseInt(profile.value.birthMonth) : null,
                    coffee_order_1: profile.value.coffee_order_1,
                    coffee_order_2: profile.value.coffee_order_2,
                    dietary_requirements: profile.value.dietary_requirements,
                    company_logo_url: profile.value.companyLogoUrl || null,
                    company_bio: profile.value.companyBio,
                    ideal_customer: profile.value.idealCustomer,
                    avoid_referring: profile.value.avoidReferring,
                    linkedin_url: profile.value.linkedinUrl || null
                };

                if (props.targetUserId) {
                    await userService.updateUser(props.targetDbId, userData);
                } else {
                    await userService.updateProfile(userData);
                }

                emit('updated', userData);
            } catch (error) {
                // Error saving profile
            }
        };

        const updatePhoto = async (photoUrl) => {
            profile.value.photo_url = photoUrl;
            emit('photo-updated', photoUrl);
        };

        const handleImageError = (e) => {
            e.target.src = DEFAULT_AVATAR;
        };

        const updateUserRole = async () => {
            try {
                const newRole = isAdmin.value ? 'admin' : 'user';
                await userService.updateProfile({
                    ...profile.value,
                    role: newRole
                });
                profile.value.role = newRole;
            } catch (error) {
                isAdmin.value = !isAdmin.value; // Revert toggle if update fails
            }
        };

        const photoUploadUserId = computed(() => {
            const id = props.targetUserId || props.currentUserId;
            return id;
        });

        const updateCompanyLogo = async (logoUrl) => {
            profile.value.companyLogoUrl = logoUrl;
        };

        const handleLogoError = (error) => {
            error.value = error.message;
            setTimeout(() => {
                error.value = '';
            }, 3000);
        };

        return {
            user,
            profile,
            isSaving,
            error,
            props,
            saveProfile,
            formatPhoneNumber,
            updatePhoto,
            handleImageError,
            DEFAULT_AVATAR,
            isAdmin,
            updateUserRole,
            photoUploadUserId,
            updateCompanyLogo,
            handleLogoError
        };
    }
};
</script>

<style scoped>
.profile-container {
    padding: 20px;
}

.profile-form {
    max-width: 800px;
    margin: 0 auto;
}

.form-group {
    margin-bottom: 20px;
}

.form-group label {
    display: block;
    margin-bottom: 5px;
    font-weight: bold;
}

.form-input {
    width: 100%;
    padding: 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
}

textarea.form-input {
    min-height: 100px;
}

.save-btn {
    background-color: #4285f4;
    color: white;
    padding: 10px 20px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.2s;
}

.save-btn:hover {
    background-color: #3367d6;
}

.save-btn:disabled {
    background-color: #ccc;
    cursor: not-allowed;
}

.error-message {
    color: #dc3545;
    white-space: pre-line; /* Show multiple errors on new lines */
    margin-top: 10px;
    font-size: 0.9em;
}

.form-input.error {
    border-color: #dc3545;
}

.form-section {
    background: #f8f9fa;
    padding: 20px;
    border-radius: 8px;
    margin-bottom: 20px;
}

.form-section h3 {
    margin: 0 0 20px 0;
    color: #2c3e50;
    font-size: 1.2em;
}

.form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
}

.form-group {
    margin-bottom: 20px;
}

.form-group:last-child {
    margin-bottom: 0;
}

.phone-input {
    display: flex;
    gap: 0.5rem;
    align-items: center;
}

.country-select {
    width: 120px;
    padding: 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
    background-color: white;
}

.form-input.phone {
    flex: 1;
}

.profile-form {
    max-width: 800px;  /* Increased from 500px */
    margin: 0 auto;
    padding: 20px;
}

.switch {
    position: relative;
    display: inline-block;
    width: 60px;
    height: 34px;
}

.switch input {
    opacity: 0;
    width: 0;
    height: 0;
}

.slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ccc;
    transition: .4s;
    border-radius: 34px;
}

.slider:before {
    position: absolute;
    content: "";
    height: 26px;
    width: 26px;
    left: 4px;
    bottom: 4px;
    background-color: white;
    transition: .4s;
    border-radius: 50%;
}

input:checked + .slider {
    background-color: #2196F3;
}

input:checked + .slider:before {
    transform: translateX(26px);
}

.role-label {
    margin-left: 70px;
    line-height: 34px;
    vertical-align: top;
}

.admin-toggle {
    display: flex;
    align-items: center;
    gap: 10px;
}

.role-label {
    font-size: 1em;
    color: #2c3e50;
}

.form-input {
    width: 100%;
    padding: 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 1em;
}

.birthday-input {
    display: flex;
    gap: 10px;
}

.day-input {
    width: 80px;
}

.month-input {
    flex: 1;
}

.modal-mode {
    padding: 0;
}

.modal-mode .profile-form {
    margin: 0;
}

/* Mobile responsive styles */
@media screen and (max-width: 768px) {
    .profile-container {
        padding-top: 80px;
        padding-left: 1rem;
        padding-right: 1rem;
    }

    .form-grid {
        grid-template-columns: 1fr;
        gap: 10px;
    }

    .birthday-input {
        flex-direction: column;
        gap: 15px;
    }

    .day-input,
    .month-input {
        width: 100%;
    }
}

.profile-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding: 0 20px;
}

.top-save-btn {
    margin-left: auto;  /* Pushes button to the right */
}
</style> 